import type Keycloak from "keycloak-js";
import type { KeycloakTokenParsed } from "keycloak-js";
import { t } from "@/language/translation";
import { captureMessageInSentry } from "@/sentry";
import { keycloak } from "./Keycloak";

const AUTHENTICATION_CALLBACK_PATH = "/auth/keycloak-callback";
const TOKEN_MIN_VALIDITY_SECONDS = 60;
const SILENT_AUTHENTICATION_TIMEOUT_MS = 15_000;
const INTERACTIVE_AUTHENTICATION_TIMEOUT_MS = 5 * 60_000;
const AUTHENTICATION_MESSAGE_TYPE = "LISA_KEYCLOAK_AUTHENTICATION_RESULT";

type AuthenticationMode = "silent" | "interactive";

type AuthenticationTokens = {
  token: string;
  refreshToken?: string;
  idToken?: string;
  timeSkew: number | null;
};

type AuthenticationResultMessage = {
  type: typeof AUTHENTICATION_MESSAGE_TYPE;
  requestId: string;
  authenticated: boolean;
  tokens?: AuthenticationTokens;
};

type TokenListener = () => void;

let refreshPromise: Promise<boolean> | undefined;
let recoveryPromise: Promise<void> | undefined;
let authenticatedSubject: string | undefined;
const tokenListeners = new Set<TokenListener>();

export class TemporaryAuthenticationError extends Error {
  constructor(message = "Authentication is temporarily unavailable", options?: ErrorOptions) {
    super(message, options);
    this.name = "TemporaryAuthenticationError";
  }
}

export class AuthenticationCancelledError extends Error {
  constructor() {
    super("Authentication was cancelled");
    this.name = "AuthenticationCancelledError";
  }
}

export class AuthenticationIdentityChangedError extends Error {
  constructor() {
    super("The authenticated user changed during session recovery");
    this.name = "AuthenticationIdentityChangedError";
  }
}

function errorStatus(error: unknown) {
  if (
    typeof error === "object" &&
    error != null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response != null &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }
  return undefined;
}

function secondsUntilTokenExpiry(instance: Keycloak) {
  if (instance.tokenParsed?.exp == null) {
    return undefined;
  }

  return Math.floor(instance.tokenParsed.exp - Date.now() / 1000 + (instance.timeSkew ?? 0));
}

function captureAuthenticationEvent(
  event: string,
  level: "info" | "warning" | "error",
  error?: unknown,
) {
  const remainingSeconds = secondsUntilTokenExpiry(keycloak);
  captureMessageInSentry(`Authentication recovery: ${event}`, {
    level,
    tags: {
      authentication_event: event,
      browser_online: window.navigator.onLine,
      document_visibility: document.visibilityState,
    },
    extra: {
      errorName: error instanceof Error ? error.name : undefined,
      errorStatus: errorStatus(error),
      remainingTokenSeconds:
        remainingSeconds == null ? undefined : Math.max(-1, Math.min(remainingSeconds, 3600)),
    },
  });
}

function isUnrecoverableRefreshError(error: unknown) {
  const status = errorStatus(error);
  return (
    status === 400 ||
    status === 401 ||
    keycloak.authenticated !== true ||
    keycloak.refreshToken == null
  );
}

function notifyTokenListeners() {
  tokenListeners.forEach((listener) => listener());
}

function rememberAuthenticatedSubject() {
  if (authenticatedSubject == null && keycloak.subject != null) {
    authenticatedSubject = keycloak.subject;
  }
}

export function initializeAuthenticationSession() {
  rememberAuthenticatedSubject();
}

export function onAuthenticationTokenChanged(listener: TokenListener) {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

async function refreshAccessToken(minValidity: number) {
  rememberAuthenticatedSubject();
  if (refreshPromise != null) {
    return refreshPromise;
  }

  refreshPromise = keycloak
    .updateToken(minValidity)
    .then((refreshed) => {
      if (refreshed) {
        notifyTokenListeners();
      }
      return refreshed;
    })
    .finally(() => {
      refreshPromise = undefined;
    });

  return refreshPromise;
}

export async function ensureFreshAccessToken(
  minValidity = TOKEN_MIN_VALIDITY_SECONDS,
): Promise<string> {
  if (!keycloak.authenticated || keycloak.token == null) {
    await recoverAuthentication();
  } else if (keycloak.isTokenExpired(minValidity)) {
    if (!window.navigator.onLine) {
      throw new TemporaryAuthenticationError("Cannot refresh authentication while offline");
    }

    try {
      await refreshAccessToken(minValidity);
    } catch (error: unknown) {
      captureAuthenticationEvent("request_refresh_failed", "warning", error);
      if (!isUnrecoverableRefreshError(error)) {
        throw new TemporaryAuthenticationError("Could not refresh authentication", {
          cause: error,
        });
      }
      await recoverAuthentication();
    }
  }

  if (!keycloak.authenticated || keycloak.token == null) {
    throw new TemporaryAuthenticationError("Authentication did not produce an access token");
  }

  return keycloak.token;
}

export async function recoverFromSessionError() {
  if (window.navigator.onLine === false) {
    throw new TemporaryAuthenticationError("Cannot recover authentication while offline");
  }

  if (keycloak.refreshToken != null) {
    try {
      await refreshAccessToken(-1);
      if (keycloak.authenticated && keycloak.token != null) {
        captureAuthenticationEvent("forced_refresh_succeeded", "info");
        return;
      }
    } catch (error: unknown) {
      captureAuthenticationEvent("forced_refresh_failed", "warning", error);
      if (!isUnrecoverableRefreshError(error)) {
        throw new TemporaryAuthenticationError("Could not contact the authentication service", {
          cause: error,
        });
      }
    }
  }

  await recoverAuthentication();
}

export async function refreshAccessTokenInBackground() {
  if (!keycloak.authenticated || keycloak.token == null) {
    await recoverAuthentication();
    return;
  }

  if (!keycloak.isTokenExpired(TOKEN_MIN_VALIDITY_SECONDS) || !window.navigator.onLine) {
    return;
  }

  await ensureFreshAccessToken();
}

function decodeToken(token: string): KeycloakTokenParsed {
  const encodedPayload = token.split(".")[1];
  if (encodedPayload == null) {
    throw new Error("Authentication token has no payload");
  }

  const normalizedPayload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
  const paddedPayload = normalizedPayload.padEnd(
    normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
    "=",
  );
  const bytes = Uint8Array.from(atob(paddedPayload), (character) => character.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as KeycloakTokenParsed;
}

export function applyRecoveredAuthenticationTokens(tokens: AuthenticationTokens) {
  const tokenParsed = decodeToken(tokens.token);
  const previousSubject = authenticatedSubject ?? keycloak.subject;

  if (previousSubject != null && tokenParsed.sub !== previousSubject) {
    captureAuthenticationEvent("identity_changed", "error");
    throw new AuthenticationIdentityChangedError();
  }

  keycloak.token = tokens.token;
  keycloak.tokenParsed = tokenParsed;
  keycloak.refreshToken = tokens.refreshToken;
  keycloak.refreshTokenParsed = tokens.refreshToken ? decodeToken(tokens.refreshToken) : undefined;
  keycloak.idToken = tokens.idToken;
  keycloak.idTokenParsed = tokens.idToken ? decodeToken(tokens.idToken) : undefined;
  keycloak.timeSkew = tokens.timeSkew;
  keycloak.sessionId = tokenParsed.sid;
  keycloak.subject = tokenParsed.sub;
  authenticatedSubject = tokenParsed.sub;
  keycloak.realmAccess = tokenParsed.realm_access;
  keycloak.resourceAccess = tokenParsed.resource_access;
  keycloak.authenticated = true;
  notifyTokenListeners();
}

function authenticationCallbackUri(requestId: string, mode: AuthenticationMode) {
  const callbackUrl = new URL(AUTHENTICATION_CALLBACK_PATH, window.location.origin);
  callbackUrl.searchParams.set("requestId", requestId);
  callbackUrl.searchParams.set("mode", mode);
  return callbackUrl.toString();
}

async function requestAuthentication(
  mode: AuthenticationMode,
  openTarget: () => Window | HTMLIFrameElement | null,
  timeoutMs: number,
) {
  const requestId = crypto.randomUUID();
  // Open the target synchronously. Interactive popups are otherwise blocked once createLoginUrl's
  // asynchronous PKCE work has yielded back to the browser.
  const target = openTarget();
  if (target == null) {
    throw new Error("Authentication window was blocked");
  }
  const authenticationTarget = target;

  return new Promise<void>((resolve, reject) => {
    const expectedSource =
      authenticationTarget instanceof HTMLIFrameElement
        ? authenticationTarget.contentWindow
        : authenticationTarget;
    let closePoll: number | undefined;
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error(`${mode} authentication timed out`));
    }, timeoutMs);

    if (!(authenticationTarget instanceof HTMLIFrameElement)) {
      closePoll = window.setInterval(() => {
        if (authenticationTarget.closed) {
          cleanup();
          reject(new AuthenticationCancelledError());
        }
      }, 500);
    }

    function cleanup() {
      window.clearTimeout(timeout);
      if (closePoll != null) {
        window.clearInterval(closePoll);
      }
      window.removeEventListener("message", receiveResult);
      if (authenticationTarget instanceof HTMLIFrameElement) {
        authenticationTarget.remove();
      } else if (!authenticationTarget.closed) {
        authenticationTarget.close();
      }
    }

    function receiveResult(event: MessageEvent<unknown>) {
      if (
        event.origin !== window.location.origin ||
        event.source !== expectedSource ||
        typeof event.data !== "object" ||
        event.data == null
      ) {
        return;
      }

      const message = event.data as Partial<AuthenticationResultMessage>;
      if (message.type !== AUTHENTICATION_MESSAGE_TYPE || message.requestId !== requestId) {
        return;
      }

      cleanup();
      if (!message.authenticated || message.tokens == null) {
        reject(new Error(`${mode} authentication was not completed`));
        return;
      }

      try {
        applyRecoveredAuthenticationTokens(message.tokens);
        resolve();
      } catch (error: unknown) {
        if (error instanceof AuthenticationIdentityChangedError) {
          // Keeping Apollo and component state from the previous identity would expose data across
          // users. A full bootstrap is intentionally required for an account switch.
          window.location.reload();
        }
        reject(error);
      }
    }

    window.addEventListener("message", receiveResult);

    keycloak
      .createLoginUrl({
        prompt: mode === "silent" ? "none" : undefined,
        redirectUri: authenticationCallbackUri(requestId, mode),
        loginHint: mode === "interactive" ? keycloak.tokenParsed?.preferred_username : undefined,
      })
      .then((loginUrl) => {
        if (authenticationTarget instanceof HTMLIFrameElement) {
          authenticationTarget.src = loginUrl;
        } else {
          authenticationTarget.location.replace(loginUrl);
        }
      })
      .catch((error: unknown) => {
        cleanup();
        reject(error);
      });
  });
}

function createSilentAuthenticationFrame() {
  const frame = document.createElement("iframe");
  frame.title = "Authentication recovery";
  frame.hidden = true;
  frame.setAttribute(
    "sandbox",
    "allow-storage-access-by-user-activation allow-scripts allow-same-origin",
  );
  frame.src = "about:blank";
  document.body.append(frame);
  return frame;
}

function openAuthenticationPopup() {
  const width = 520;
  const height = 720;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  return window.open(
    "about:blank",
    "lisa-keycloak-login",
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
  );
}

function requestInteractiveAuthentication() {
  return new Promise<void>((resolve, reject) => {
    let authenticationStarted = false;
    if (window.$confirm == null) {
      reject(new TemporaryAuthenticationError("Authentication dialog is not available"));
      return;
    }

    window.$confirm.require({
      position: "center",
      modal: true,
      header: t("api.toast.error.title").toString(),
      message: t("api.toast.error.expiredSession").toString(),
      acceptLabel: t("modules.oneDrive.buttons.login.title").toString(),
      rejectLabel: t("global.cancel").toString(),
      accept: () => {
        authenticationStarted = true;
        requestAuthentication(
          "interactive",
          openAuthenticationPopup,
          INTERACTIVE_AUTHENTICATION_TIMEOUT_MS,
        ).then(resolve, reject);
      },
      reject: () => reject(new AuthenticationCancelledError()),
      onHide: () => {
        if (!authenticationStarted) {
          reject(new AuthenticationCancelledError());
        }
      },
    });
  });
}

export async function recoverAuthentication() {
  if (recoveryPromise != null) {
    return recoveryPromise;
  }

  recoveryPromise = (async () => {
    if (!window.navigator.onLine) {
      throw new TemporaryAuthenticationError("Cannot recover authentication while offline");
    }

    try {
      await requestAuthentication(
        "silent",
        createSilentAuthenticationFrame,
        SILENT_AUTHENTICATION_TIMEOUT_MS,
      );
      captureAuthenticationEvent("silent_reauthentication_succeeded", "info");
      return;
    } catch (error: unknown) {
      captureAuthenticationEvent("silent_reauthentication_failed", "warning", error);
    }

    try {
      await requestInteractiveAuthentication();
      captureAuthenticationEvent("interactive_reauthentication_succeeded", "info");
    } catch (error: unknown) {
      captureAuthenticationEvent("interactive_reauthentication_failed", "warning", error);
      throw error;
    }
  })().finally(() => {
    recoveryPromise = undefined;
  });

  return recoveryPromise;
}

export function isAuthenticationCallbackPage() {
  return window.location.pathname.replace(/\/+$/, "") === AUTHENTICATION_CALLBACK_PATH;
}

export async function completeAuthenticationCallback() {
  const requestId = new URLSearchParams(window.location.search).get("requestId") ?? "";
  const target = window.opener ?? (window.parent !== window ? window.parent : null);

  let authenticated = false;
  try {
    authenticated = await keycloak.init({ checkLoginIframe: false, pkceMethod: "S256" });
  } catch (error: unknown) {
    captureAuthenticationEvent("authentication_callback_failed", "warning", error);
  }

  const message: AuthenticationResultMessage = {
    type: AUTHENTICATION_MESSAGE_TYPE,
    requestId,
    authenticated,
    tokens:
      authenticated && keycloak.token != null
        ? {
            token: keycloak.token,
            refreshToken: keycloak.refreshToken,
            idToken: keycloak.idToken,
            timeSkew: keycloak.timeSkew,
          }
        : undefined,
  };

  target?.postMessage(message, window.location.origin);
  if (window.opener != null) {
    window.close();
  }
}
